import * as React from "react";
import { useNavigate } from "@opendash/router";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  Card,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";
import styled from "styled-components";

// Seitenspezifische Styled Components
const CalloutBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%);
  border-left: 4px solid #5c6bc0;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "HINWEIS";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #5c6bc0;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #283593;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

const StatContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  margin: 24px 0;
`;

const StatBox = styled.div`
  background: linear-gradient(135deg, var(--primaryColor) 0%, #1976d2 100%);
  color: white;
  padding: 20px 24px;
  border-radius: 12px;
  text-align: center;
  flex: 1;
  min-width: 200px;

  .stat-number {
    font-size: 36px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .stat-label {
    font-size: 14px;
    opacity: 0.9;
  }
`;

export const VerteilnetzRoute = React.memo(function VerteilnetzRoute() {
  const navigate = useNavigate();
  const { t, getKeysWithPrefix } = useTexts();

  const literaturNumbers = React.useMemo(() => {
    const textKeys = getKeysWithPrefix("B.3.literatur.");
    const numbers = new Set<number>();
    textKeys.forEach((key) => {
      const match = key.match(/^B\.3\.literatur\.(\d+)\./);
      if (match) {
        numbers.add(parseInt(match[1], 10));
      }
    });
    return Array.from(numbers).sort((a, b) => a - b);
  }, [getKeysWithPrefix]);

  const renderParagraphs = (text: string) => {
    if (!text) return null;
    return text
      .split("\n\n")
      .map((paragraph, index) => (
        <p key={index}>{processInlineFormatting(paragraph)}</p>
      ));
  };

  const tabs = [
    {
      title: t("B.tab.1.title", "Wie funktioniert ein Wasserwerk?"),
      path: "/wasserklaerer/wasseraufbereitung/wasserwerk",
      isActive: false,
    },
    {
      title: t("B.tab.2.title", "Trinkwasserqualität"),
      path: "/wasserklaerer/wasseraufbereitung/qualitaet",
      isActive: false,
    },
    {
      title: t("B.tab.3.title", "Verteilnetz des Trinkwassers"),
      path: "/wasserklaerer/wasseraufbereitung/verteilnetz",
      isActive: true,
    },
  ];

  return (
    <AppLayout>
      <ContentContainer>
        <h1>
          {t("B.3.title", "Das Verteilnetz des Trinkwassers in Deutschland")}
        </h1>

        <TabContainer>
          {tabs.map((tab, index) => (
            <TabButton
              key={index}
              isActive={tab.isActive}
              onClick={() => !tab.isActive && navigate(tab.path)}
            >
              {tab.title}
            </TabButton>
          ))}
        </TabContainer>

        {/* Allgemeines */}
        <Card>
          <h2>{t("B.3.allgemein.title", "Allgemeines")}</h2>

          {renderParagraphs(t("B.3.allgemein.text1", ""))}

          <StatContainer>
            <StatBox>
              <div className="stat-number">
                {t("B.3.stat1.number", "530.000 km")}
              </div>
              <div className="stat-label">
                {t("B.3.stat1.label", "Leitungsnetz in Deutschland")}
              </div>
            </StatBox>
            <StatBox>
              <div className="stat-number">{t("B.3.stat2.number", "99%")}</div>
              <div className="stat-label">
                {t("B.3.stat2.label", "der Bevölkerung angeschlossen")}
              </div>
            </StatBox>
          </StatContainer>

          {renderParagraphs(t("B.3.allgemein.text2", ""))}

          <h3>
            {t("B.3.anlagen.title", "Zu den Wasserverteilungsanlagen zählen")}
          </h3>
          <p>{processInlineFormatting(t("B.3.anlagen.1", ""))}</p>
          <p>{processInlineFormatting(t("B.3.anlagen.2", ""))}</p>
          <p>{processInlineFormatting(t("B.3.anlagen.3", ""))}</p>

          <CalloutBox>
            <h4>{t("B.3.investition.title", "Hoher Investitionsbedarf")}</h4>
            {renderParagraphs(t("B.3.investition.text", ""))}
          </CalloutBox>
        </Card>

        {/* Herausforderungen */}
        <Card>
          <h2>
            {t(
              "B.3.herausforderungen.title",
              "Herausforderungen des Verteilnetzes"
            )}
          </h2>

          <CalloutBox>
            <h4>
              {t(
                "B.3.herausforderungen.infrastruktur.title",
                "Veraltete Infrastruktur"
              )}
            </h4>
            {renderParagraphs(t("B.3.herausforderungen.text1", ""))}
          </CalloutBox>

          {renderParagraphs(t("B.3.herausforderungen.text2", ""))}
        </Card>

        {/* Investitionen */}
        <Card>
          <h2>
            {t(
              "B.3.investitionen.title",
              "Warum sind Investitionen jetzt so wichtig?"
            )}
          </h2>
          {renderParagraphs(t("B.3.investitionen.text1", ""))}
          {renderParagraphs(t("B.3.investitionen.text2", ""))}
        </Card>

        {/* Zuständigkeit */}
        <Card>
          <h2>
            {t(
              "B.3.zustaendigkeit.title",
              "Ab welcher Grenze ist der Grundstückseigentümer zuständig?"
            )}
          </h2>
          {renderParagraphs(t("B.3.zustaendigkeit.text", ""))}
        </Card>

        {/* Literatur */}
        <SourcesSection>
          <h3>{t("B.3.literatur.title", "Literatur")}</h3>
          <ol>
            {literaturNumbers.map((num) => (
              <li key={num} id={`quelle-${num}`}>
                <LinkifyText>
                  {[
                    t(`B.3.literatur.${num}.text`, ""),
                    t(`B.3.literatur.${num}.url`, ""),
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </LinkifyText>
              </li>
            ))}
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
